/**
 * Reference options for a bid's line-item pickers, read from that bid's FROZEN RATE CARD (P5,
 * design §9: "Rate pickers for a bid read /bids/<id>/rate-card (snapshot), never the live catalog").
 *
 * Why it matters: the backend already guarantees a bid prices from its frozen card. If the UI kept
 * offering LIVE catalog rates, an estimator editing a six-month-old bid would pick today's rate and
 * freeze it onto a line — quietly re-pricing history through the front door, defeating the very
 * invariant P5 exists to establish.
 *
 * Fallback: a bid created BEFORE P5 has no card (404). Those fall back to the live catalog, because
 * there is no frozen truth to honour and an empty picker would just make old bids uneditable. That
 * is not the lie `fetch_rate_card` refuses to tell — the server never presents live data AS a
 * frozen card; this is only which options the picker offers.
 *
 * A financially-blind caller is refused the card (403) and gets no options — correct, since they
 * cannot mutate bids either.
 */
import { useEffect, useState } from 'react';
import HttpService from '../../AppSettings/services/HttpService';
import { getBidUuid } from '../bidStore';
import { CatalogOption, loadCatalogOptions } from './useCatalogOptions';

const http = new HttpService();

/** snapshot key + how a row becomes an option, per picker kind. Cost codes carry no rate —
 * they are the line's ledger identity, not its price (gap review 1.6). */
const FROM_SNAPSHOT: Record<string, { key: string; rateField?: string; label: (row: any) => string }> = {
  equipment: { key: 'equipment', rateField: 'default_isr', label: (r) => String(r.name ?? r.id) },
  labour: { key: 'labour', rateField: 'default_rate', label: (r) => String(r.name ?? r.id) },
  'cost-codes': { key: 'cost_codes', label: (r) => `${r.code} — ${r.name ?? r.code}` },
};

const cache = new Map<string, CatalogOption[]>();
const inflight = new Map<string, Promise<CatalogOption[]>>();

function fromSnapshot(snapshot: any, kind: string): CatalogOption[] {
  const spec = FROM_SNAPSHOT[kind];
  if (!spec) return [];
  return (snapshot?.[spec.key] ?? []).map((row: any) => {
    const raw = spec.rateField === undefined ? NaN : Number(row[spec.rateField]);
    return {
      id: String(row.id),
      label: spec.label(row),
      rate: Number.isFinite(raw) ? raw : undefined,
      // The frozen snapshot carries the full catalog row, category included — surfaced the
      // same way the live fallback surfaces it, so a pre-P5 bid's picker groups identically.
      category: row.category ? String(row.category) : undefined,
    };
  });
}

async function fetchForBid(bidNumber: string, kind: string): Promise<CatalogOption[]> {
  const uuid = getBidUuid(bidNumber);
  if (!uuid) return loadCatalogOptions(kind);   // not hydrated yet — live is the only option
  try {
    const res = await http.get<any>(`/bids/${uuid}/rate-card`);
    return fromSnapshot(res?.data?.rate_card?.snapshot, kind);
  } catch (err: any) {
    // 404 = a pre-P5 bid with no frozen card -> live catalog (see the module docstring).
    // 403 = financially blind -> no options, which is correct: they cannot edit the bid either.
    if (err?.status === 403) return [];
    return loadCatalogOptions(kind);
  }
}

export function loadRateCardOptions(bidNumber: string, kind: string): Promise<CatalogOption[]> {
  const cacheKey = `${bidNumber}:${kind}`;
  const cached = cache.get(cacheKey);
  if (cached) return Promise.resolve(cached);

  const pending = inflight.get(cacheKey);
  if (pending) return pending;

  const promise = fetchForBid(bidNumber, kind)
    .then((options) => {
      cache.set(cacheKey, options);
      return options;
    })
    .catch(() => [] as CatalogOption[])
    .finally(() => inflight.delete(cacheKey));

  inflight.set(cacheKey, promise);
  return promise;
}


export function useRateCardOptions(bidNumber: string, kind: string): CatalogOption[] {
  const [options, setOptions] = useState<CatalogOption[]>(
    () => cache.get(`${bidNumber}:${kind}`) ?? [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!bidNumber) return undefined;
    void loadRateCardOptions(bidNumber, kind).then((loaded) => {
      if (!cancelled) setOptions(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [bidNumber, kind]);

  return options;
}

// ── Phase templates (gap review 1.2) ────────────────────────────────────────────────────────────

/** A phase template as the add-phase picker consumes it: enough to SEED a phase — name,
 * production rate, and the crew lines (desc/kind/qty/hrs/rate) the admin configured. */
export interface PhaseTemplateOption {
  id: string;
  name: string;
  productionPerDayM?: number;
  crew: { desc: string; kind: 'equipment' | 'labour'; qty: number; hrs: number; rate?: number }[];
}

function templateFromRow(row: any): PhaseTemplateOption {
  const rate = Number(row.default_production_rate);
  const crew = Array.isArray(row.default_crew?.crew) ? row.default_crew.crew : [];
  return {
    id: String(row.id),
    name: String(row.name ?? row.id),
    productionPerDayM: Number.isFinite(rate) ? rate : undefined,
    crew: crew
      .filter((c: any) => c && (c.kind === 'equipment' || c.kind === 'labour'))
      .map((c: any) => ({
        desc: String(c.desc ?? ''),
        kind: c.kind,
        qty: Number(c.qty) || 0,
        hrs: Number(c.hrs) || 0,
        rate: Number.isFinite(Number(c.rate)) ? Number(c.rate) : undefined,
      })),
  };
}

const templateCache = new Map<string, PhaseTemplateOption[]>();

/** The bid's frozen templates (same snapshot discipline as the rate pickers). Live-catalog
 * fallback ONLY when the bid has no card at all (pre-P5, 404) — a card whose template list is
 * legitimately EMPTY stays empty, or a bid frozen before templates existed would offer templates
 * authored after the freeze (the review's point). A blind caller reaches the fallback too; the
 * open-but-stripped catalog gives them names only. */
export function usePhaseTemplateOptions(bidNumber: string): PhaseTemplateOption[] {
  const [options, setOptions] = useState<PhaseTemplateOption[]>(
    () => templateCache.get(bidNumber) ?? [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!bidNumber) return undefined;
    void (async () => {
      const uuid = getBidUuid(bidNumber);
      let rows: any[] = [];
      let cardMissing = !uuid;
      try {
        if (uuid) {
          const res = await http.get<any>(`/bids/${uuid}/rate-card`);
          rows = res?.data?.rate_card?.snapshot?.phase_templates ?? [];
        }
      } catch (err: any) {
        // 404 = pre-P5 bid with no frozen card; anything else (incl. 403) is NOT license to
        // substitute live data for a frozen truth.
        cardMissing = err?.status === 404;
        rows = [];
      }
      if (cardMissing) {
        try {
          const { listCatalog } = await import('./referenceDataService');
          rows = await listCatalog('/phase-templates', true);
        } catch {
          rows = [];
        }
      }
      const loaded = rows.map(templateFromRow);
      templateCache.set(bidNumber, loaded);
      if (!cancelled) setOptions(loaded);
    })();
    return () => { cancelled = true; };
  }, [bidNumber]);

  return options;
}
